import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import {
  Button,
  Group,
  Modal,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useBrandsStore } from "./brandsStore";
import { useBrandContext } from "./brandContext";

export default function CreateBrand({
  modalOpened,
  setModalOpened,
  initialValues,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setLocation] = useLocation();
  const { createBrand, updateBrand } = useBrandsStore();
  const { setSelectedBrand, fetchBrands } = useBrandContext();

  const isEdit = !!initialValues?.id;

  const { t } = useTranslation("brands", {
    keyPrefix: "createModal",
  });

  const form = useForm({
    initialValues: {
      name: initialValues?.name || "",
      websiteUrl: initialValues?.websiteUrl || "",
      description: initialValues?.description || "",
    },
    validate: {
      name: (value) =>
        value.trim().length > 0 ? null : t("validation.nameRequired", "Brand name is required"),
    },
  });

  useEffect(() => {
    form.setInitialValues({
      name: initialValues?.name || "",
      websiteUrl: initialValues?.websiteUrl || "",
      description: initialValues?.description || "",
    });
    form.setValues({
      name: initialValues?.name || "",
      websiteUrl: initialValues?.websiteUrl || "",
      description: initialValues?.description || "",
    });
  }, [initialValues]);

  const handleSubmit = (values) => {
    setIsSubmitting(true);
    if (isEdit) {
      updateBrand(initialValues.id, values)
        .then((updatedBrand) => {
          notifications.show({
            title: t("notifications.updateSuccessTitle", "Brand Updated"),
            message: t("notifications.updateSuccessMessage", {
              name: updatedBrand.name,
              defaultValue: "{{name}} has been updated.",
            }),
            color: "green",
          });
          setModalOpened(false);
          form.reset();
          setLocation(`/b/edit/${updatedBrand.id}`);
        })
        .catch((error) => {
          console.error("Failed to update brand:", error);
          notifications.show({
            title: t("notifications.updateErrorTitle", "Update Failed"),
            message: t("notifications.updateErrorMessage", "Could not update the brand. Please try again."),
            color: "red",
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      createBrand(values)
        .then((brand) => {
          notifications.show({
            title: t("notifications.createSuccessTitle", "Brand Created"),
            message: t("notifications.createSuccessMessage", {
              name: brand.name,
              defaultValue: "{{name}} has been created.",
            }),
            color: "green",
          });
          setModalOpened(false);
          form.reset();
          
          // Refresh the brands list and select the newly created brand
          fetchBrands().then(() => {
            setSelectedBrand(brand);
            setLocation("/settings?tab=setup");
          });
        })
        .catch((error) => {
          console.error("Failed to create brand:", error);
          notifications.show({
            title: t("notifications.createErrorTitle", "Creation Failed"),
            message: t("notifications.createErrorMessage", "Could not create the brand. Please try again."),
            color: "red",
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <Modal
      opened={modalOpened}
      onClose={() => {
        setModalOpened(false);
        form.reset();
      }}
      title={t(isEdit ? "editTitle" : "title", "Create Brand")}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label={t("labels.name", "Name")}
            placeholder={t("placeholders.name", "Enter brand name")}
            required
            {...form.getInputProps("name")}
          />
          <TextInput
            label="Website URL"
            placeholder="https://example.com"
            {...form.getInputProps("websiteUrl")}
          />
          <Textarea
            label={t("labels.description", "Description")}
            placeholder={t(
              "placeholders.description",
              "Enter brand description"
            )}
            {...form.getInputProps("description")}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setModalOpened(false);
                form.reset();
              }}
              disabled={isSubmitting}
            >
              {t("buttons.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isEdit
                ? t("buttons.save", "Save")
                : t("buttons.create", "Create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
